import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
// ── Types ──
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
  visible: boolean;
}
// ── Static Data ──
const NAMES = ['Martín', 'Lucía', 'Santiago', 'Valentina', 'Mateo', 'Camila', 'Nicolás', 'Isabella', 'Sebastián', 'Sofía', 'Diego', 'Florencia', 'Tomás', 'Julieta', 'Agustín', 'Carolina', 'Facundo', 'Milagros', 'Joaquín', 'Rocío', 'Lautaro', 'Abril', 'Gonzalo', 'Pilar', 'Ignacio', 'Catalina', 'Ramiro', 'Antonella', 'Federico', 'Micaela', 'Emiliano', 'Brenda', 'Maximiliano', 'Aldana', 'Lucas', 'Candela', 'Bruno', 'Morena', 'Franco', 'Jazmín'];
const CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'San Miguel de Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Corrientes', 'Posadas', 'Bahía Blanca', 'Paraná', 'San Luis', 'Tandil', 'Bariloche', 'Ushuaia', 'Río Cuarto', 'Junín', 'Quilmes', 'Lanús'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'El Método Barrita Pro',
  editionLabel: 'Pack Completo',
  priceNumber: 16990,
  priceText: '$16.990',
  thumbnailUrl: 'https://cdn.shopify.com/s/files/1/0752/3211/6894/files/FOTO_BUNDLE.png?v=1776370777'
}];
// ── Product Scanner ──
function scanPageProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  // Strategy 1: JSON-LD
  try {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(script => {
      const data = JSON.parse(script.textContent || '');
      if (data['@type'] === 'Product' || data.offers) {
        const offer = data.offers?.[0] || data.offers || {};
        products.push({
          title: data.name || 'Product',
          editionLabel: data.category || '',
          priceNumber: parseFloat(offer.price) || 0,
          priceText: offer.priceCurrency ? `${offer.priceCurrency} ${offer.price}` : `$${offer.price}`,
          thumbnailUrl: (Array.isArray(data.image) ? data.image[0] : data.image) || ''
        });
      }
    });
  } catch {
    /* ignore */
  }
  if (products.length > 0) return products;
  // Strategy 2: Scan price sections on the page
  try {
    const priceRegex = /\$\s?([\d.,]+)/g;
    const allText = document.body.innerText || '';
    const priceMatches = [...allText.matchAll(priceRegex)];
    // Look for the main product image
    const heroImg = document.querySelector('.hero-img, [alt*="Bundle"], [alt*="bundle"], [alt*="Barrita"]') as HTMLImageElement;
    const mainThumb = heroImg?.src || '';
    // Look for price boxes / sections
    const priceBoxes = document.querySelectorAll('[class*="price"], [id*="comprar"], [class*="Price"]');
    priceBoxes.forEach(box => {
      const text = box.textContent || '';
      const match = text.match(/\$\s?([\d.,]+)/);
      if (match) {
        const priceStr = match[1].replace(/\./g, '').replace(',', '.');
        const priceNum = parseFloat(priceStr);
        if (priceNum > 0 && priceNum < 1000000) {
          // Find nearby title
          const heading = box.querySelector('h1, h2, h3');
          const title = heading?.textContent?.trim() || 'El Método Barrita Pro';
          // Find nearby image
          const img = box.querySelector('img') as HTMLImageElement;
          const thumb = img?.src || mainThumb;
          products.push({
            title,
            editionLabel: 'Pack Completo',
            priceNumber: priceNum,
            priceText: `$${match[1]}`,
            thumbnailUrl: thumb
          });
        }
      }
    });
    // Deduplicate by price
    const seen = new Set<number>();
    const unique = products.filter(p => {
      if (seen.has(p.priceNumber)) return false;
      seen.add(p.priceNumber);
      return true;
    });
    if (unique.length > 0) return unique;
  } catch {
    /* ignore */
  }
  // Strategy 3: Look for bonus items as additional products
  try {
    const bonusCards = document.querySelectorAll('[class*="bonus-card"], [class*="Bonus"]');
    bonusCards.forEach(card => {
      const title = card.querySelector('h3')?.textContent?.trim();
      const img = card.querySelector('img') as HTMLImageElement;
      if (title && img?.src) {
        products.push({
          title,
          editionLabel: 'Bono Gratis',
          priceNumber: 0,
          priceText: 'GRATIS',
          thumbnailUrl: img.src
        });
      }
    });
  } catch {
    /* ignore */
  }
  return products.length > 0 ? products : FALLBACK_PRODUCTS;
}
// ── Component ──
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const productsRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const counterRef = useRef(0);
  const prefersReducedMotion = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    const handler = (e: MediaQueryListEvent) => {
      prefersReducedMotion.current = e.matches;
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const pickName = useCallback((): string => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pool[Math.floor(Math.random() * pool.length)];
    history.push(name);
    if (history.length > 10) history.shift();
    return name;
  }, []);
  const pickCity = useCallback((): string => {
    return CITIES[Math.floor(Math.random() * CITIES.length)];
  }, []);
  const pickProduct = useCallback((): ScannedProduct => {
    const catalog = productsRef.current;
    if (catalog.length === 0) return FALLBACK_PRODUCTS[0];
    // Filter to paid products for notifications (skip free bonuses most of the time)
    const paid = catalog.filter(p => p.priceNumber > 0);
    const pool = paid.length > 0 ? paid : catalog;
    return pool[Math.floor(Math.random() * pool.length)];
  }, []);
  const showToast = useCallback(() => {
    const id = ++counterRef.current;
    const data: ToastData = {
      id,
      name: pickName(),
      city: pickCity(),
      product: pickProduct(),
      visible: true
    };
    setToast(data);
    // Hide after 4s
    setTimeout(() => {
      setToast(prev => prev?.id === id ? {
        ...prev,
        visible: false
      } : prev);
    }, 4000);
    // Remove from DOM after animation
    setTimeout(() => {
      setToast(prev => prev?.id === id ? null : prev);
    }, 4500);
  }, [pickName, pickCity, pickProduct]);
  useEffect(() => {
    // Scan products after a short delay to let page render
    const scanTimer = setTimeout(() => {
      productsRef.current = scanPageProducts();
    }, 500);
    // First toast after 3s
    const firstTimer = setTimeout(() => {
      showToast();
    }, 3000);
    // Recurring toasts
    let intervalId: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      const delay = 8000 + Math.random() * 7000; // 8-15s
      intervalId = setTimeout(() => {
        showToast();
        scheduleNext();
      }, delay);
    };
    const startTimer = setTimeout(() => {
      scheduleNext();
    }, 7500); // Start recurring after first toast finishes
    return () => {
      clearTimeout(scanTimer);
      clearTimeout(firstTimer);
      clearTimeout(startTimer);
      clearTimeout(intervalId);
    };
  }, [showToast]);
  if (!toast) return null;
  const reduced = prefersReducedMotion.current;
  return <div className="fixed bottom-6 left-1/2 z-[9999] pointer-events-none" style={{
    transform: 'translateX(-50%)'
  }} data-id="element-204">
      <div className="pointer-events-auto bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.15),0_2px_8px_rgba(0,0,0,0.08)] border border-[#E2E8F0] px-4 py-3 flex items-center gap-3 max-w-[420px] w-[calc(100vw-32px)]" style={{
      transition: reduced ? 'none' : 'opacity 0.4s ease, transform 0.4s ease',
      opacity: toast.visible ? 1 : 0,
      transform: toast.visible ? 'translateY(0)' : 'translateY(16px)'
    }} data-id="element-205">
        {/* Thumbnail */}
        {toast.product.thumbnailUrl && <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-[#F1F5F9]" data-id="element-206">
            <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-full h-full object-cover" data-id="element-207" />
          </div>}

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-208">
          <p className="text-[13px] leading-[1.4] text-[#0F172A]" data-id="element-209">
            <span className="font-semibold" data-id="element-210">{toast.name}</span>
            <span className="text-[#94A3B8]" data-id="element-211"> en </span>
            <span className="font-medium" data-id="element-212">{toast.city}</span>
            <span className="text-[#94A3B8]" data-id="element-213"> compró </span>
            <span className="font-semibold text-[#15803D]" data-id="element-214">
              {toast.product.editionLabel || toast.product.title}
            </span>
          </p>
          <p className="text-[12px] text-[#94A3B8] mt-0.5 flex items-center gap-1.5" data-id="element-215">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" data-id="element-216" />
            {toast.product.priceText}
            <span className="mx-1" data-id="element-217">·</span>
            hace unos segundos
          </p>
        </div>

        {/* Verified badge */}
        <div className="flex-shrink-0 text-[#22C55E]" data-id="element-218">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-219">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" data-id="element-220" />
            <polyline points="22 4 12 14.01 9 11.01" data-id="element-221" />
          </svg>
        </div>
      </div>
    </div>;
}